import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Row, Col, Button, message, Space, Typography } from "antd";
import { SaveOutlined, ArrowLeftOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { NumericFormat } from "react-number-format";
import { Method_POST_CONTEXTO } from "../../../../../service/api";
import dayjs from "dayjs";
import { useDraggableModal } from "../../Balancete/utils/modalDraggable";

const { Option } = Select;
const { Text } = Typography;

export function ModalEditarLoteLivroRazao({ open, onClose, lancamentos, contasContabeis, onSaved, zIndex, onFocus, getContainer = () => document.body }) {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        open,
        modalKey: 'editRazaoLote',
        initialWidth: 700,
        initialHeight: 550,
        overflow: 'visible'
    });
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [contasInternas, setContasInternas] = useState(contasContabeis || []);

    useEffect(() => {
        if (open && (!contasContabeis || contasContabeis.length === 0)) {
            fetchContas();
        } else {
            setContasInternas(contasContabeis);
        }
    }, [open, contasContabeis]);

    const fetchContas = async () => {
        try {
            const res = await Method_POST_CONTEXTO("/empresa/contabilidade/conta-contabil/listar");
            setContasInternas(Array.isArray(res) ? res : (res?.data ?? []));
        } catch (err) {
            console.error(err);
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Busca os detalhes completos de cada lançamento para garantir que temos as partidas e contas corretas
            const fullLancamentos = await Promise.all(
                lancamentos.map(l => Method_POST_CONTEXTO("/empresa/contabilidade/lancamento/consultar", { id: l.id }))
            );

            const parseToDouble = (value) => {
                if (!value) return 0;
                if (typeof value === 'number') return value;
                const cleanValue = String(value).replace("R$ ", "").replace(/\./g, "").replace(",", ".");
                return parseFloat(cleanValue) || 0;
            };

            const payloads = fullLancamentos.map((l) => {
                const valorSimples = values.valor ? parseToDouble(values.valor) : l.valor;

                // Agora temos os dados completos vindos do 'consultar'
                const originalDebito = l.partidas?.find(p => p.tipo === "D");
                const originalCredito = l.partidas?.find(p => p.tipo === "C");

                const contaDebitoId = values.contaDebito || originalDebito?.contaContabil?.id;
                const contaCreditoId = values.contaCredito || originalCredito?.contaContabil?.id;

                if (!contaDebitoId || !contaCreditoId) {
                    throw new Error(`Não foi possível identificar as contas originais do lançamento ${l.id}.`);
                }

                const partidasFormatadas = [
                    { tipo: "D", contaContabil: { id: contaDebitoId }, valor: valorSimples },
                    { tipo: "C", contaContabil: { id: contaCreditoId }, valor: valorSimples }
                ];

                const dRef = dayjs(l.dataReferencia);

                return {
                    id: l.id,
                    dataReferencia: dRef.valueOf(),
                    nome: values.nome || l.nome,
                    idGrupo: l.idGrupo,
                    partidas: partidasFormatadas,
                    ano: dRef.year(),
                    mes: dRef.month() + 1
                };
            });

            await Promise.all(payloads.map(p => Method_POST_CONTEXTO("/empresa/contabilidade/lancamento/atualizar", p)));

            message.success(`${lancamentos.length} lançamentos atualizados com sucesso!`);
            onSaved?.();
            onClose();
            form.resetFields();
        } catch (err) {
            console.error(err);
            message.error("Erro ao atualizar lançamentos.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={700}
            footer={null}
            title={
                <Space>
                    <EditOutlined style={{ color: '#1890ff' }} />
                    <span>Edição em Lote ({lancamentos?.length} itens)</span>
                </Space>
            }
            className="razao-premium-modal"
            getContainer={getContainer}
            {...draggableProps}
            zIndex={zIndex}
            styles={{ body: { overflow: 'visible' } }}
        >
            <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f0f9ff', borderRadius: '8px', borderLeft: '4px solid #0ea5e9' }}>
                <Space align="start">
                    <InfoCircleOutlined style={{ color: '#0ea5e9', marginTop: 3 }} />
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                        Campos preenchidos abaixo serão aplicados a <b>todos</b> os lançamentos selecionados.
                        Deixe em branco para manter os valores originais.
                    </Text>
                </Space>
            </div>

            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="contaDebito" label="Nova Conta Débito">
                            <Select placeholder="Manter original" showSearch optionFilterProp="children" allowClear>
                                {contasInternas.map(c => <Option key={c.id} value={c.id}>{c.codigo} - {c.nome}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="contaCredito" label="Nova Conta Crédito">
                            <Select placeholder="Manter original" showSearch optionFilterProp="children" allowClear>
                                {contasInternas.map(c => <Option key={c.id} value={c.id}>{c.codigo} - {c.nome}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="nome" label="Novo Histórico / Nome">
                            <Input placeholder="Manter original" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="valor" label="Novo Valor Fixo">
                            <NumericFormat customInput={Input} placeholder="Manter original" thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2} fixedDecimalScale />
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                    <Button onClick={onClose} size="large">Cancelar</Button>
                    <Button type="primary" size="large" icon={<SaveOutlined />} onClick={handleSave} loading={loading} className="btn-gradient">
                        Aplicar Alterações
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}
